import { L10n } from '~commonInfra/L10n/L10n';
import { Formatters, Messages } from '~src/codegen/Localize';

import { IJobHistoryItemDTO, IJobHistoryItemVM, JobHistoryStatus, JobHistoryStatusBadgeColor } from './IJobHistory';

const MS_PER_MINUTE = 60000;

const formatTimestamp = (epochMs: number | null): string | null => {
  // Treat any non-finite value (null/undefined/NaN) as absent so the cell shows a dash instead of
  // an Invalid Date; 0 is a real epoch and is kept.
  if (epochMs == null || !Number.isFinite(epochMs)) {
    return null;
  }
  return Formatters.formatMediumDateTimeWithoutSeconds(new Date(epochMs));
};

const formatDuration = (durationMs: number | null): string | null => {
  // Same non-finite guard as formatTimestamp; 0 is a real duration and is kept.
  if (durationMs == null || !Number.isFinite(durationMs)) {
    return null;
  }
  return Messages.taskDetailsDurationMinutes({ MINUTES: L10n.localizedFloat(durationMs / MS_PER_MINUTE) });
};

const getStatusBadgeColor = (status: JobHistoryStatus): JobHistoryStatusBadgeColor => {
  switch (status) {
    case 'Completed':
    case 'BridgeExtractionCompleted':
      return 'green';
    case 'Failed':
      return 'red';
    case 'InProgress':
      return 'yellow';
    case 'Pending':
    case 'Cancelled':
      return 'gray';
    default:
      // Fail safe: an unmapped/new backend status renders neutral rather than dropping the icon.
      return 'gray';
  }
};

const getStatusMessageTooltip = (statusMessage: string | null): string | null => {
  // The cell clips long messages, so surface the full text via the hover tooltip whenever a
  // message is present.
  if (statusMessage == null || statusMessage === '') {
    return null;
  }
  return statusMessage;
};

// Composes the single "Bridge" column's lines (Option B / Figma): per pool, an optional
// "Pool: {pool}/Client: {client}" line. Prefers the structured multi-pool poolAgentMap (all pools,
// sorted by pool name for a stable order matching the popup) and falls back to the legacy first-pool
// scalars when an older server omits the map (no routing rules in that fallback -- the map is the
// only source). Returns null when the run carries no Bridge info, which also gates the column's
// visibility. If Bridge routing Rules are used we will show "Routing Rule Ids: {ids}" comma delimited at the end
const buildBridgeLines = (
  poolAgentMap: Record<string, string> | null | undefined,
  poolToRoutingRuleMap: Record<string, string> | null | undefined,
  bridgePool: string | null,
  bridgeClient: string | null
): string[] | null => {
  if (poolAgentMap != null) {
    const pools = Object.keys(poolAgentMap);
    if (pools.length > 0) {
      const lines: string[] = [];
            // Stable display order via localeCompare. Note this can diverge from the backend scalar's
      // Java code-point .sorted() for pool names differing only by punctuation (e.g. "Pool-A" vs
      // "Pool_A"); harmless here since the two never render together (the scalar is only used in the
      // no-map fallback below, which produces a single line), and this path sorts the whole map itself.
      const sortedPools = pools.sort((left, right) => left.localeCompare(right));
      sortedPools.forEach(pool => {
        lines.push(
          Messages.taskDetailsHistoryBridgePoolClient({
            BRIDGE_POOL_NAME: pool,
            BRIDGE_CLIENT_NAME: poolAgentMap[pool],
          })
        );
      });

      if (poolToRoutingRuleMap != null) {
        const uniqueRoutingRuleIds: string[] = [];
        sortedPools.forEach(pool => {
          const routingRuleIdList = poolToRoutingRuleMap[pool];
          if (routingRuleIdList != null && routingRuleIdList !== '') {
            routingRuleIdList.split(',').forEach(routingRuleId => {
              const trimmedRoutingRuleId = routingRuleId.trim();
              if (trimmedRoutingRuleId && uniqueRoutingRuleIds.indexOf(trimmedRoutingRuleId) === -1) {
                uniqueRoutingRuleIds.push(trimmedRoutingRuleId);
              }
            });
          }
        });

        if (uniqueRoutingRuleIds.length > 0) {
          lines.push(
            Messages.taskDetailsHistoryBridgeRoutingRuleIds({
              ROUTING_RULE_ID_LIST: uniqueRoutingRuleIds.join(', '),
            })
          );
        }
      }
      return lines;
    }
  }
  if (bridgePool != null || bridgeClient != null) {
    return [
      Messages.taskDetailsHistoryBridgePoolClient({
        BRIDGE_POOL_NAME: bridgePool ?? '',
        BRIDGE_CLIENT_NAME: bridgeClient ?? '',
      }),
    ];
  }
  return null;
};

export class JobHistoryAdapter {
  public static toViewModel(dto: IJobHistoryItemDTO): IJobHistoryItemVM {
    return {
      jobId: dto.jobId,
      luid: dto.luid,
      status: dto.status,
      statusBadgeColor: getStatusBadgeColor(dto.status),
      statusMessage: dto.statusMessage,
      statusMessageTooltip: getStatusMessageTooltip(dto.statusMessage),
      scheduledAt: formatTimestamp(dto.scheduledAt),
      startedAt: formatTimestamp(dto.startedAt),
      completedAt: formatTimestamp(dto.completedAt),
      completedAtEpoch: dto.completedAt,
      queueTime: formatDuration(dto.queueTime),
      runTime: formatDuration(dto.runTime),
      bridgePool: dto.bridgePool,
      bridgeClient: dto.bridgeClient,
      bridgeLines: buildBridgeLines(dto.poolAgentMap, dto.poolToRoutingRuleMap, dto.bridgePool, dto.bridgeClient),
    };
  }

  public static toSortedViewModels(dtos: IJobHistoryItemDTO[]): IJobHistoryItemVM[] {
    const models = dtos.map(dto => this.toViewModel(dto));
    return models.sort((left, right) => {
      const leftTs = left.completedAtEpoch ?? Number.POSITIVE_INFINITY;
      const rightTs = right.completedAtEpoch ?? Number.POSITIVE_INFINITY;
      return rightTs - leftTs;
    });
  }

  public static mergeSortedPage(
    existing: ReadonlyArray<IJobHistoryItemVM>,
    newDtos: IJobHistoryItemDTO[]
  ): IJobHistoryItemVM[] {
    const newModels = newDtos.map(dto => this.toViewModel(dto));
    newModels.sort((left, right) => {
      const leftTs = left.completedAtEpoch ?? Number.POSITIVE_INFINITY;
      const rightTs = right.completedAtEpoch ?? Number.POSITIVE_INFINITY;
      return rightTs - leftTs;
    });
    const merged: IJobHistoryItemVM[] = [];
    let i = 0;
    let j = 0;
    while (i < existing.length && j < newModels.length) {
      const existingTs = existing[i].completedAtEpoch ?? Number.POSITIVE_INFINITY;
      const newTs = newModels[j].completedAtEpoch ?? Number.POSITIVE_INFINITY;
      if (existingTs >= newTs) {
        merged.push(existing[i]);
        i++;
      } else {
        merged.push(newModels[j]);
        j++;
      }
    }
    while (i < existing.length) {
      merged.push(existing[i]);
      i++;
    }
    while (j < newModels.length) {
      merged.push(newModels[j]);
      j++;
    }
    return merged;
  }
}
